import React, { useState, useEffect, useCallback } from 'react';
import { getLocalStorageItem } from '@utils/storageUtils';

export type ThemeType = 'light' | 'dark';

type ThemeStateType = {
    theme: ThemeType;
    setTheme: (theme: ThemeType) => void;
};

export const initialThemeState: ThemeStateType = {
    theme: 'dark',
    setTheme: (theme: ThemeType) => null,
};

const ThemeContext = React.createContext<ThemeStateType>(initialThemeState);

export const useTheme = () => {
    const ctx = React.useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme must be used inside MyThemeProvider');
    }
    return ctx;
};

const DARK_THEME_QUERY = '(prefers-color-scheme: dark)';

const MyThemeProvider = ({ children }) => {
    const savedTheme = getLocalStorageItem('Replan.theme');
    const [theme, setTheme] = useState<ThemeType>(
        savedTheme ? savedTheme : window.matchMedia(DARK_THEME_QUERY).matches ? 'dark' : 'light'
    );

    useEffect(() => {
        const savedTheme = getLocalStorageItem('Replan.theme');
        const mediaQueryList = window.matchMedia(DARK_THEME_QUERY);

        const handleMatch = () => {
            const theme = mediaQueryList.matches ? 'dark' : 'light';
            setTheme(theme);
            document.documentElement.setAttribute('data-theme', curTheme);
            localStorage.removeItem('Replan.theme');
        };

        mediaQueryList.addEventListener('change', handleMatch);

        const curTheme = savedTheme ? savedTheme : mediaQueryList.matches ? 'dark' : 'light';
        setTheme(curTheme);
        document.documentElement.setAttribute('data-theme', curTheme);

        return () => mediaQueryList.removeEventListener('change', handleMatch);
    }, []);

    const updateTheme = useCallback((theme: ThemeType) => {
        setTheme(theme);
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('Replan.theme', theme);
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, setTheme: updateTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export default MyThemeProvider;
